import React, { useState } from 'react';
import { bookingsAPI } from '../utils/api';

function NoShowChargeModal({ booking, isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [chargePercentage, setChargePercentage] = useState(100);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!isOpen || !booking) return null;

  const chargeAmount = (booking.service_price * chargePercentage / 100).toFixed(2);

  const handleCharge = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await bookingsAPI.chargeNoshow(booking.id, {
        amount_percentage: chargePercentage
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process charge');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result) {
      onSuccess?.();
    }
    setResult(null);
    setError(null);
    setChargePercentage(100);
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 2000 }}
      onClick={handleClose}
    >
      <div
        className="modal-content"
        style={{ maxWidth: '480px', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            padding: '1.25rem 1.5rem',
            background: result ? '#F0F7F1' : '#FEE9E9'
          }}
        >
          <h3 style={{
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: '600',
            color: result ? '#4A7C59' : '#C62828',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {result ? (
              <><span style={{ fontSize: '1.25rem' }}>&#10003;</span> Charge Successful</>
            ) : (
              'No-Show Charge'
            )}
          </h3>
        </div>

        {/* Body */}
        <div className="modal-body">
          {result ? (
            // Success State
            <div>
              <div style={{
                background: '#F0F7F1',
                border: '1px solid #A8C9AD',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#4A7C59' }}>
                  £{result.amount_charged?.toFixed(2)}
                </p>
                <p style={{ margin: '0.25rem 0 0', color: '#4A7C59', fontSize: '0.875rem' }}>
                  Successfully charged to customer's card
                </p>
              </div>
              <p style={{ margin: 0, color: '#6B6B6B', fontSize: '0.875rem' }}>
                The booking has been marked as a no-show and the customer's card has been charged.
              </p>
            </div>
          ) : (
            // Charge Form
            <>
              {/* Booking Details */}
              <div style={{
                background: '#FAF8F5',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B6B6B' }}>Customer</span>
                    <span style={{ fontWeight: '500' }}>{booking.customer_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B6B6B' }}>Service</span>
                    <span style={{ fontWeight: '500' }}>{booking.service_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B6B6B' }}>Date</span>
                    <span style={{ fontWeight: '500' }}>{booking.booking_date}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B6B6B' }}>Time</span>
                    <span style={{ fontWeight: '500' }}>{booking.start_time}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #E8E4E0',
                    paddingTop: '0.5rem',
                    marginTop: '0.25rem'
                  }}>
                    <span style={{ color: '#6B6B6B' }}>Service Price</span>
                    <span style={{ fontWeight: '600', color: '#2C2C2C' }}>£{booking.service_price}</span>
                  </div>
                </div>
              </div>

              {/* Charge Amount Selection */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#2C2C2C' }}>
                  Charge Amount
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {[50, 75, 100].map(pct => (
                    <button
                      key={pct}
                      onClick={() => setChargePercentage(pct)}
                      style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        border: chargePercentage === pct ? '2px solid #C62828' : '1px solid #D4CBC3',
                        background: chargePercentage === pct ? '#FEE9E9' : 'white',
                        color: chargePercentage === pct ? '#C62828' : '#2C2C2C',
                        fontWeight: '500',
                        cursor: 'pointer',
                        flex: 1,
                        minWidth: '80px'
                      }}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount to Charge */}
              <div style={{
                background: '#FEE9E9',
                border: '1px solid #E8A0A0',
                borderRadius: '8px',
                padding: '1rem',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, color: '#C62828', fontSize: '0.875rem' }}>Amount to charge</p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '2rem', fontWeight: '700', color: '#C62828' }}>
                  £{chargeAmount}
                </p>
              </div>

              {error && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  background: '#FEE9E9',
                  border: '1px solid #E8A0A0',
                  borderRadius: '8px',
                  color: '#C62828',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              <p style={{
                margin: '1rem 0 0',
                color: '#6B6B6B',
                fontSize: '0.8rem',
                lineHeight: '1.5'
              }}>
                This will charge the customer's saved card and mark the booking as a no-show.
                This action cannot be undone.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className="modal-footer"
          style={{ background: '#FAF8F5' }}
        >
          {result ? (
            <button
              onClick={handleClose}
              className="btn btn-primary"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={handleClose}
                disabled={loading}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCharge}
                disabled={loading}
                className="btn btn-danger"
              >
                {loading ? 'Processing...' : `Charge £${chargeAmount}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default NoShowChargeModal;
